/**
 * useDashboardData Hook
 * Fetches all BRR sessions from the session store and provides KPI stats,
 * filtered session list, and filter controls for the dashboard.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { SessionAuditClient, SessionStatus, computeDashboardStats, computeAnalyticsBreakdown, BRRApiClient } from '../services';
import { DashboardStats, AnalyticsBreakdown } from '../services/sessionMetrics';
import { BRRSession, LotReleaseInfo, UserRole } from '../services/types';
import { isAtLeast } from '../utils/userRole';

export type StatusFilter = 'all' | 'in-progress' | 'delayed' | 'submitted' | 'completed' | 'rejected';

export type TimeWindow = '24h' | '7d' | '30d' | '90d' | '1y' | 'all';

export type BatchTypeFilter = 'all' | 'direct' | 'indirect';

export type PriorityFilter = 'all' | 'low' | 'medium' | 'high';

export type DelayReasonFilter = 'all' | 'delayed' | 'no_inspection_lot' | 'missing_cmo_documents' | 'missing_serialisation' | 'other';

export type DelayCategory = 'green' | 'amber' | 'red' | null;

const TIME_WINDOW_MS: Record<TimeWindow, number | null> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
  '1y': 365 * 24 * 60 * 60 * 1000,
  'all': null,
};

export interface DashboardFilters {
  status: StatusFilter;
  search: string;
  timeWindow: TimeWindow;
  batchType: BatchTypeFilter;
  priority: PriorityFilter;
  delayReason: DelayReasonFilter;
  cmoFilter: string;
  productFilter: string;
  userFilter: string;
}

interface UseDashboardDataOptions {
  sessionClient: SessionAuditClient;
  apiClient?: BRRApiClient;
  userEmail?: string;
  userRole?: UserRole;
}

interface UseDashboardDataReturn {
  stats: DashboardStats;
  analytics: AnalyticsBreakdown | null;
  sessions: BRRSession[];
  filteredSessions: BRRSession[];
  filters: DashboardFilters;
  setFilters: (filters: Partial<DashboardFilters>) => void;
  loading: boolean;
  error: Error | null;
  isTruncated: boolean;
  refresh: () => void;
  releaseDates: Record<string, any>;
  releaseDatesLoading: boolean;
  distinctUsers: string[];
  distinctCmos: string[];
  distinctProducts: string[];
}

const IN_PROGRESS_STATUSES = [SessionStatus.Draft, SessionStatus.InProgress];

const STATUS_FILTER_MAP: Record<StatusFilter, number | number[] | null> = {
  all: null,
  'in-progress': IN_PROGRESS_STATUSES,
  delayed: IN_PROGRESS_STATUSES,
  submitted: SessionStatus.Submitted,
  completed: SessionStatus.Completed,
  rejected: SessionStatus.Rejected,
};

const DEFAULT_STATS: DashboardStats = {
  totalInPipeline: 0,
  avgProcessingTimeDays: 0,
  completedCount: 0,
  pendingActions: 0,
};

/**
 * Extract unique inspection lot IDs from session details JSON.
 */
export function extractLotIds(sessions: BRRSession[], detailsColumn: string): string[] {
  const lotIds: string[] = [];
  for (const s of sessions) {
    try {
      const details = s[detailsColumn];
      if (details) {
        const parsed = JSON.parse(details);
        if (parsed?.inspectionLotId) {
          lotIds.push(String(parsed.inspectionLotId));
        }
      }
    } catch { /* ignore parse errors */ }
  }
  return [...new Set(lotIds)];
}

function extractLotIdFromSession(s: BRRSession, detailsColumn: string): string | null {
  try {
    const d = s[detailsColumn];
    if (d) {
      const p = JSON.parse(d);
      if (p?.inspectionLotId) return String(p.inspectionLotId);
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Check if a batch ID is valid (not a temporary placeholder).
 * TEMP- prefixed IDs are created before the user selects a real batch
 * and should not be displayed in dashboard or included in stats.
 */
function isValidBatchId(batchId: string | undefined): boolean {
  return !!batchId && !batchId.startsWith('TEMP-');
}

export type ReleaseStatus = 'pending' | 'released' | 'rejected' | 'pre-released' | null;

export interface ReleaseDelayResult {
  hours: number | null;
  category: DelayCategory;
  status: ReleaseStatus;
  code: string | null;
  text: string | null;
}

/**
 * Compute the delay between BRR submission and SAP usage decision, with colour category
 * and release status from the usage decision code.
 */
export function computeReleaseDelay(
  submittedAt: string | null | undefined,
  releaseInfo: { usageDecisionDatetime: string | null; usageDecisionCode: string | null; usageDecisionText: string | null; releaseStatus: string } | string | null | undefined
): ReleaseDelayResult {
  const empty: ReleaseDelayResult = { hours: null, category: null, status: null, code: null, text: null };

  if (!releaseInfo) return empty;

  // Backward compat: if releaseInfo is a plain datetime string (old API shape)
  const dt = typeof releaseInfo === 'string' ? releaseInfo : releaseInfo.usageDecisionDatetime;
  const code = typeof releaseInfo === 'string' ? null : (releaseInfo.usageDecisionCode ?? null);
  const text = typeof releaseInfo === 'string' ? null : (releaseInfo.usageDecisionText ?? null);
  const status: ReleaseStatus = typeof releaseInfo === 'string' ? 'released' : (releaseInfo.releaseStatus as ReleaseStatus) ?? 'released';

  if (!submittedAt || !dt) return { ...empty, status: status === 'rejected' ? 'rejected' : null, code, text };

  const submittedMs = new Date(submittedAt).getTime();
  const releaseMs = new Date(dt).getTime();
  if (isNaN(submittedMs) || isNaN(releaseMs)) return { ...empty, status, code, text };

  const hours = (releaseMs - submittedMs) / (1000 * 60 * 60);
  if (hours < 0) return { hours, category: null, status: 'pre-released', code, text };
  let category: DelayCategory;
  if (hours < 24) category = 'green';
  else if (hours <= 72) category = 'amber';
  else category = 'red';
  return { hours, category, status, code, text };
}


export const useDashboardData = ({
  sessionClient,
  apiClient,
  userEmail,
  userRole = 'viewer',
}: UseDashboardDataOptions): UseDashboardDataReturn => {
  const [sessions, setSessions] = useState<BRRSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [filters, setFiltersState] = useState<DashboardFilters>({
    status: 'all',
    search: '',
    timeWindow: '30d',
    batchType: 'all',
    priority: 'all',
    delayReason: 'all',
    cmoFilter: 'all',
    productFilter: 'all',
    userFilter: 'all',
  });
  const [releaseDates, setReleaseDates] = useState<Record<string, LotReleaseInfo | null>>({});
  const [releaseDatesLoading, setReleaseDatesLoading] = useState(false);

  const columns = sessionClient.columns;

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { sessions: fetchedSessions, isTruncated: truncated } = await sessionClient.getAllSessions();
      setSessions(fetchedSessions);
      setIsTruncated(truncated);

      // Enrich with release dates if apiClient is provided
      if (apiClient) {
        const lotIds = extractLotIds(fetchedSessions, columns.details);
        if (lotIds.length > 0) {
          setReleaseDatesLoading(true);
          try {
            const resp = await apiClient.getLotReleaseDates(lotIds);
            setReleaseDates(resp.releaseDates);

            // Auto-sync: transition Submitted sessions that SAP has decided on
            const toSync = fetchedSessions.filter((s) => {
              if (s[columns.status] !== SessionStatus.Submitted) return false;
              const lid = extractLotIdFromSession(s, columns.details);
              if (!lid) return false;
              const info = resp.releaseDates[lid];
              if (!info?.usageDecisionDatetime) return false;
              return info.releaseStatus === 'released' || info.releaseStatus === 'rejected';
            });
            if (toSync.length > 0) {
              await Promise.allSettled(
                toSync.map((s) => {
                  const lid = extractLotIdFromSession(s, columns.details)!;
                  const info = resp.releaseDates[lid]!;
                  const sid = s[columns.sessionid];
                  const bid = s[columns.batchid];
                  return sessionClient.logSapCompletion(
                    bid, sid, info.usageDecisionDatetime!, info.releaseStatus as 'released' | 'rejected'
                  ).catch((syncErr) => {
                    console.warn('SAP sync failed for session', sid, syncErr);
                  });
                })
              );
              setSessions((prev) =>
                prev.map((s) => {
                  const lid = extractLotIdFromSession(s, columns.details);
                  if (!lid) return s;
                  const info = resp.releaseDates[lid];
                  if (!info?.usageDecisionDatetime) return s;
                  if (s[columns.status] !== SessionStatus.Submitted) return s;
                  if (info.releaseStatus !== 'released' && info.releaseStatus !== 'rejected') return s;
                  return {
                    ...s,
                    [columns.status]: info.releaseStatus === 'released'
                      ? SessionStatus.Completed
                      : SessionStatus.Rejected,
                    [columns.completedat]: info.usageDecisionDatetime,
                  };
                })
              );
            }
          } catch (err) {
            console.warn('Failed to fetch release dates:', err);
          } finally {
            setReleaseDatesLoading(false);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [sessionClient, apiClient, columns.details]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Filter out TEMP placeholder sessions (created before user selects a real batch)
  // This must happen before any other processing so counts, stats, etc. exclude them
  const validSessions = useMemo(() => {
    return sessions.filter((s) => isValidBatchId(s[columns.batchid]));
  }, [sessions, columns]);

  const parsedDetailsMap = useMemo(() => {
    const map = new WeakMap<any, any>();
    for (const s of validSessions) {
      try {
        const details = s[columns.details];
        if (details) map.set(s, JSON.parse(details));
      } catch { /* ignore */ }
    }
    return map;
  }, [validSessions, columns]);

  const getParsedDetails = (s: any): any => parsedDetailsMap.get(s) ?? null;

  const distinctUsers = useMemo(() => {
    const emails = new Set<string>();
    for (const s of validSessions) {
      const email = s[columns.useremail];
      if (email) emails.add(email);
    }
    return [...emails].sort();
  }, [validSessions, columns]);

  const filteredSessions = useMemo(() => {
    let result = validSessions;

    if (isAtLeast(userRole, 'supervisor') && filters.userFilter !== 'all') {
      const lowerFilter = filters.userFilter.toLowerCase();
      result = result.filter((s) => (s[columns.useremail] || '').toLowerCase() === lowerFilter);
    }

    // Time window filter on startedat
    const windowMs = TIME_WINDOW_MS[filters.timeWindow];
    if (windowMs !== null) {
      const cutoff = Date.now() - windowMs;
      result = result.filter((s) => {
        const started = s[columns.startedat];
        if (!started) return false;
        return new Date(started).getTime() >= cutoff;
      });
    }

    if (filters.status === 'delayed') {
      result = result.filter((s) => {
        if (!IN_PROGRESS_STATUSES.includes(s[columns.status])) return false;
        const parsed = getParsedDetails(s);
        return !!parsed?.delayReason;
      });
    } else if (filters.status === 'in-progress') {
      result = result.filter((s) => {
        if (!IN_PROGRESS_STATUSES.includes(s[columns.status])) return false;
        const parsed = getParsedDetails(s);
        return !parsed?.delayReason;
      });
    } else {
      const statusCode = STATUS_FILTER_MAP[filters.status];
      if (statusCode !== null) {
        const codes = Array.isArray(statusCode) ? statusCode : [statusCode];
        result = result.filter((s) => codes.includes(s[columns.status]));
      }
    }

    if (filters.batchType !== 'all') {
      result = result.filter((s) => {
        const parsed = getParsedDetails(s);
        return parsed?.batchType === filters.batchType;
      });
    }

    if (filters.priority !== 'all') {
      result = result.filter((s) => {
        const parsed = getParsedDetails(s);
        if (!parsed) return false;
        const sessionPriority = parsed.priority === 'normal' ? 'medium' : parsed.priority;
        return sessionPriority === filters.priority;
      });
    }

    if (filters.delayReason !== 'all') {
      result = result.filter((s) => {
        const parsed = getParsedDetails(s);
        if (!parsed) return false;
        if (filters.delayReason === 'delayed') return !!parsed.delayReason;
        return parsed.delayReason === filters.delayReason;
      });
    }

    if (filters.cmoFilter !== 'all') {
      result = result.filter((s) => {
        const parsed = getParsedDetails(s);
        return parsed?.delayCmoName === filters.cmoFilter;
      });
    }

    if (filters.productFilter !== 'all') {
      const term = filters.productFilter.toLowerCase();
      result = result.filter((s) => {
        const productName = (s[columns.productname] || '').toLowerCase();
        if (productName === term) return true;
        const parsed = getParsedDetails(s);
        return (parsed?.productName || '').toLowerCase() === term;
      });
    }

    if (filters.search.trim()) {
      const term = filters.search.toLowerCase().trim();
      result = result.filter((s) => {
        const batchId = (s[columns.batchid] || '').toLowerCase();
        const product = (s[columns.productname] || '').toLowerCase();
        const email = (s[columns.useremail] || '').toLowerCase();
        const qp = (s[columns.assignedqp] || '').toLowerCase();
        if (batchId.includes(term) || product.includes(term) || email.includes(term) || qp.includes(term)) return true;
        const parsed = getParsedDetails(s);
        if (parsed) {
          const lotId = String(parsed.inspectionLotId || '').toLowerCase();
          if (lotId.includes(term)) return true;
          const materialId = String(parsed.materialId || '').toLowerCase();
          if (materialId.includes(term)) return true;
          const vendorBatchId = String(parsed.vendorBatchId || '').toLowerCase();
          if (vendorBatchId.includes(term)) return true;
        }
        return false;
      });
    }

    return result;
  }, [validSessions, filters, columns, userRole, userEmail, parsedDetailsMap]);

  const stats = useMemo(
    () =>
      filteredSessions.length > 0
        ? computeDashboardStats(filteredSessions, {
            status: columns.status,
            startedat: columns.startedat,
            completedat: columns.completedat,
            details: columns.details,
          })
        : DEFAULT_STATS,
    [filteredSessions, columns]
  );

  const analytics = useMemo((): AnalyticsBreakdown | null => {
    if (!isAtLeast(userRole, 'supervisor')) return null;
    return computeAnalyticsBreakdown(filteredSessions, {
      status: columns.status,
      startedat: columns.startedat,
      completedat: columns.completedat,
      useremail: columns.useremail,
      details: columns.details,
    });
  }, [filteredSessions, columns, userRole]);

  const distinctCmos = useMemo(() => {
    const cmos = new Set<string>();
    for (const s of validSessions) {
      const parsed = getParsedDetails(s);
      if (parsed?.delayCmoName) cmos.add(parsed.delayCmoName);
    }
    return [...cmos].sort();
  }, [validSessions, parsedDetailsMap]);

  const distinctProducts = useMemo(() => {
    const products = new Set<string>();
    for (const s of validSessions) {
      const name = s[columns.productname];
      if (name) { products.add(name); continue; }
      const parsed = getParsedDetails(s);
      if (parsed?.productName) products.add(parsed.productName);
    }
    return [...products].sort();
  }, [validSessions, columns, parsedDetailsMap]);

  const setFilters = useCallback((partial: Partial<DashboardFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
  }, []);

  return {
    stats,
    analytics,
    sessions: validSessions,
    filteredSessions,
    filters,
    setFilters,
    loading,
    error,
    isTruncated,
    refresh: fetchSessions,
    releaseDates,
    releaseDatesLoading,
    distinctUsers,
    distinctCmos,
    distinctProducts,
  };
};
